import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Save, Upload, X, Image as ImageIcon, ShieldCheck, MapPin } from 'lucide-react';
import { useData } from '../context/DataContext';
import LocationPicker from './LocationPicker';
import BusinessHoursEditor from './BusinessHoursEditor';
import { DEFAULT_HOURS } from '../lib/businessHours';

const Field = ({ label, htmlFor, required, hint, children }) => (
  <div>
    <Label htmlFor={htmlFor} className="text-sm">
      {label}{required && <span className="text-red-500"> *</span>}
    </Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const VendorProfileForm = ({ vendor, apiBase, token, onSaved }) => {
  const { toast } = useToast();
  const { tree } = useData();
  const [form, setForm] = useState({
    name: '', contact_name: '', email: '', city: '', address: '',
    latitude: null, longitude: null,
    gst: '', established: '', business_type: '', employees: '',
    website: '', description: '', logo_url: '', categories: [],
    business_hours: DEFAULT_HOURS,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!vendor) return;
    setForm({
      name: vendor.name || '',
      contact_name: vendor.contact_name || '',
      email: vendor.email || '',
      city: vendor.city || '',
      address: vendor.address || '',
      latitude: vendor.latitude ?? null,
      longitude: vendor.longitude ?? null,
      gst: vendor.gst || '',
      established: vendor.established || vendor.year_established || '',
      business_type: vendor.business_type || '',
      employees: vendor.employees || '',
      website: vendor.website || '',
      description: vendor.description || '',
      logo_url: vendor.logo_url || '',
      categories: vendor.categories || [],
      business_hours: vendor.business_hours && Object.keys(vendor.business_hours).length ? vendor.business_hours : DEFAULT_HOURS,
    });
  }, [vendor]);

  // Flatten category names from the tree (main + sub) for the checkbox list
  const allCategories = React.useMemo(() => {
    const names = new Set();
    (tree || []).forEach((m) => (m.categories || []).forEach((c) => {
      if (c.name) names.add(c.name);
    }));
    return Array.from(names).sort();
  }, [tree]);

  const toggleCategory = (name) => {
    setForm((f) => {
      const has = f.categories.includes(name);
      return { ...f, categories: has ? f.categories.filter((c) => c !== name) : [...f.categories, name] };
    });
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Logo too large', description: 'Maximum 5 MB', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch(`${apiBase}/api/vendor/me/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Upload failed');
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast({ title: 'Logo uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) return toast({ title: 'Company name is required', variant: 'destructive' });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      return toast({ title: 'Invalid email', variant: 'destructive' });
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        established: form.established ? parseInt(form.established, 10) : null,
      };
      const resp = await fetch(`${apiBase}/api/vendor/me`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.detail || 'Save failed');
      toast({ title: 'Profile updated' });
      onSaved?.(data);
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="vendor-profile-form">
      {/* Logo + identity */}
      <div className="bg-white border border-gray-200 rounded-md p-5">
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-[#ee5e23]" /> Brand & identity
        </h3>
        <div className="flex items-start gap-5 flex-wrap">
          <div className="shrink-0">
            <div className="w-28 h-28 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center overflow-hidden bg-gray-50">
              {form.logo_url ? (
                <img src={form.logo_url} alt="logo" className="w-full h-full object-cover" data-testid="vendor-profile-logo-preview" />
              ) : (
                <ImageIcon className="w-8 h-8 text-gray-300" />
              )}
            </div>
            <label className="mt-2 inline-flex items-center gap-1 text-xs text-[#2c54b2] cursor-pointer hover:underline">
              <Upload className="w-3 h-3" /> {form.logo_url ? 'Change logo' : 'Upload logo'} (max 5 MB)
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="vendor-profile-logo-input" />
            </label>
            {uploading && <div className="text-xs text-gray-500 mt-1">Uploading...</div>}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 flex-1 min-w-[260px]">
            <Field label="Company Name" htmlFor="vp_name" required>
              <Input id="vp_name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="vendor-profile-name-input" />
            </Field>
            <Field label="Contact Person" htmlFor="vp_contact_name">
              <Input id="vp_contact_name" value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                data-testid="vendor-profile-contact-input" />
            </Field>
            <Field label="Mobile (login)" htmlFor="vp_mobile" hint="Mobile is your login ID and cannot be changed. Contact admin to update.">
              <Input id="vp_mobile" value={vendor?.mobile || ''} disabled className="bg-gray-50 cursor-not-allowed" />
            </Field>
            <Field label="Email" htmlFor="vp_email" hint="Used for enquiry alerts & welcome emails.">
              <Input id="vp_email" type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="vendor-profile-email-input" />
            </Field>
          </div>
        </div>
      </div>

      {/* Business details */}
      <div className="bg-white border border-gray-200 rounded-md p-5">
        <h3 className="text-base font-semibold mb-4 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-600" /> Business details
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="GST Number" htmlFor="vp_gst" hint="15-character GSTIN. Shown as 'Verified' on your storefront.">
            <Input id="vp_gst" value={form.gst} maxLength={15}
              onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase() })}
              data-testid="vendor-profile-gst-input" />
          </Field>
          <Field label="Year Established" htmlFor="vp_established">
            <Input id="vp_established" type="number" value={form.established} min={1900} max={2026}
              onChange={(e) => setForm({ ...form, established: e.target.value })}
              data-testid="vendor-profile-established-input" />
          </Field>
          <Field label="Business Type" htmlFor="vp_business_type">
            <select id="vp_business_type"
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={form.business_type}
              onChange={(e) => setForm({ ...form, business_type: e.target.value })}
              data-testid="vendor-profile-business-type-select">
              <option value="">Select type</option>
              {['Manufacturer','Wholesaler','Distributor','Retailer','Trader','Service Provider','Exporter','Importer'].map((bt) => (
                <option key={bt} value={bt}>{bt}</option>
              ))}
            </select>
          </Field>
          <Field label="Employees" htmlFor="vp_employees">
            <select id="vp_employees"
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={form.employees}
              onChange={(e) => setForm({ ...form, employees: e.target.value })}
              data-testid="vendor-profile-employees-select">
              <option value="">Select range</option>
              {['1-10','11-50','51-200','201-500','500+'].map((e2) => <option key={e2} value={e2}>{e2}</option>)}
            </select>
          </Field>
          <Field label="City" htmlFor="vp_city">
            <Input id="vp_city" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              data-testid="vendor-profile-city-input" />
          </Field>
          <Field label="Website" htmlFor="vp_website" hint="We&apos;ll prepend https:// automatically.">
            <Input id="vp_website" value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="www.yourbrand.com"
              data-testid="vendor-profile-website-input" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Business Location" htmlFor="vp_location" hint="Search a place, then drag the orange pin to the exact spot. The chosen address + map pin replaces the typed address everywhere on your storefront.">
            <LocationPicker
              value={{ address: form.address, latitude: form.latitude, longitude: form.longitude }}
              onChange={({ address, latitude, longitude }) => setForm((f) => ({ ...f, address, latitude, longitude }))}
            />
          </Field>
          <div className="mt-2">
            <Label className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Override address text (optional)</Label>
            <Textarea
              id="vp_address"
              rows={2}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Auto-filled from the map. Edit if you want a friendlier display address."
              data-testid="vendor-profile-address-input"
              className="mt-1"
            />
          </div>
        </div>
        <div className="mt-4">
          <Field label="About / Description" htmlFor="vp_description">
            <Textarea id="vp_description" rows={3} value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Briefly describe what your business does — appears on your storefront."
              data-testid="vendor-profile-description-input" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Business Hours" htmlFor="vp_hours" hint="Buyers see an 'Open now' badge on your storefront based on these times.">
            <BusinessHoursEditor
              value={form.business_hours}
              onChange={(next) => setForm((f) => ({ ...f, business_hours: next }))}
              testidPrefix="vendor-hours"
            />
          </Field>
        </div>
      </div>

      {/* Categories */}
      <div className="bg-white border border-gray-200 rounded-md p-5">
        <h3 className="text-base font-semibold mb-1">Categories you supply</h3>
        <p className="text-xs text-gray-500 mb-3">
          Selected categories help buyers find you in the directory and via search filters.
        </p>
        {allCategories.length === 0 ? (
          <div className="text-sm text-gray-500">No categories available.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
            {allCategories.map((c) => {
              const checked = form.categories.includes(c);
              return (
                <label key={c} className={`flex items-center gap-2 text-sm px-2 py-1.5 rounded cursor-pointer border ${checked ? 'border-[#ee5e23] bg-orange-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleCategory(c)} data-testid={`vendor-profile-cat-${c}`} />
                  <span className="truncate">{c}</span>
                </label>
              );
            })}
          </div>
        )}
        {form.categories.length > 0 && (
          <div className="mt-3 text-xs text-gray-600">
            {form.categories.length} selected:{' '}
            {form.categories.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 bg-orange-100 text-[#c34a14] px-2 py-0.5 rounded-full mr-1 mb-1">
                {c}
                <button type="button" onClick={() => toggleCategory(c)} className="hover:text-red-600" data-testid={`vendor-profile-remove-cat-${c}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 py-3 -mx-1 px-1 z-10">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="vendor-profile-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Profile'}
        </Button>
      </div>
    </form>
  );
};

export default VendorProfileForm;
